import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

function JsonComparePage() {
  const { theme } = useTheme();
  const [json1, setJson1] = useState('');
  const [json2, setJson2] = useState('');
  const [diffResult, setDiffResult] = useState(null);
  const [error, setError] = useState('');

  const parseJSON = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      throw new Error('Invalid JSON: ' + e.message);
    }
  };

  const compareJSON = (obj1, obj2, path = '') => {
    const diff = {
      added: [],
      removed: [],
      changed: [],
      unchanged: []
    };

    const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
    
    allKeys.forEach(key => {
      const currentPath = path ? `${path}.${key}` : key;
      const val1 = obj1?.[key];
      const val2 = obj2?.[key];

      if (val1 === undefined && val2 !== undefined) {
        diff.added.push({ path: currentPath, value: val2 });
      } else if (val1 !== undefined && val2 === undefined) {
        diff.removed.push({ path: currentPath, value: val1 });
      } else if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
        const nestedDiff = compareJSON(val1, val2, currentPath);
        diff.added.push(...nestedDiff.added);
        diff.removed.push(...nestedDiff.removed);
        diff.changed.push(...nestedDiff.changed);
        diff.unchanged.push(...nestedDiff.unchanged);
      } else if (val1 !== val2) {
        diff.changed.push({ path: currentPath, oldValue: val1, newValue: val2 });
      } else {
        diff.unchanged.push({ path: currentPath, value: val1 });
      }
    });

    return diff;
  };

  const handleCompare = () => {
    setError('');
    setDiffResult(null);

    try {
      if (!json1.trim() || !json2.trim()) {
        throw new Error('Please provide both JSON documents');
      }

      const obj1 = parseJSON(json1);
      const obj2 = parseJSON(json2);
      
      const diff = compareJSON(obj1, obj2);
      setDiffResult(diff);
    } catch (e) {
      setError(e.message);
    }
  };

  const renderValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      return <span className="text-purple-400">{JSON.stringify(value)}</span>;
    }
    if (typeof value === 'string') {
      return <span className="text-green-400">"{value}"</span>;
    }
    if (typeof value === 'number') {
      return <span className="text-blue-400">{value}</span>;
    }
    if (typeof value === 'boolean') {
      return <span className="text-yellow-400">{String(value)}</span>;
    }
    return <span>{String(value)}</span>;
  };

  const getThemeClasses = () => {
    const baseClasses = {
      ocean: 'bg-blue-50 text-gray-900',
      sunset: 'bg-orange-50 text-gray-900',
      forest: 'bg-green-50 text-gray-900',
      midnight: 'bg-gray-900 text-gray-100'
    };
    return baseClasses[theme] || baseClasses.ocean;
  };

  const getInputThemeClasses = () => {
    const baseClasses = {
      ocean: 'bg-white border-gray-300',
      sunset: 'bg-white border-gray-300',
      forest: 'bg-white border-gray-300',
      midnight: 'bg-gray-800 border-gray-600 text-gray-100'
    };
    return baseClasses[theme] || baseClasses.ocean;
  };

  const getSummaryCardThemeClasses = () => {
    const baseClasses = {
      ocean: 'bg-white',
      sunset: 'bg-white',
      forest: 'bg-white',
      midnight: 'bg-gray-800'
    };
    return baseClasses[theme] || baseClasses.ocean;
  };

  const getSummaryItemThemeClasses = (type) => {
    const baseClasses = {
      ocean: {
        added: 'bg-green-100 text-green-600',
        removed: 'bg-red-100 text-red-600',
        changed: 'bg-yellow-100 text-yellow-600',
        unchanged: 'bg-gray-100 text-gray-600'
      },
      sunset: {
        added: 'bg-green-100 text-green-600',
        removed: 'bg-red-100 text-red-600',
        changed: 'bg-yellow-100 text-yellow-600',
        unchanged: 'bg-gray-100 text-gray-600'
      },
      forest: {
        added: 'bg-green-100 text-green-600',
        removed: 'bg-red-100 text-red-600',
        changed: 'bg-yellow-100 text-yellow-600',
        unchanged: 'bg-gray-100 text-gray-600'
      },
      midnight: {
        added: 'bg-green-900 text-green-400',
        removed: 'bg-red-900 text-red-400',
        changed: 'bg-yellow-900 text-yellow-400',
        unchanged: 'bg-gray-700 text-gray-400'
      }
    };
    return baseClasses[theme]?.[type] || baseClasses.ocean[type];
  };

  const getDiffSectionThemeClasses = (type) => {
    const baseClasses = {
      ocean: {
        card: 'bg-white',
        item: 'bg-green-50',
        border: 'border-green-500',
        title: 'text-green-600'
      },
      sunset: {
        card: 'bg-white',
        item: 'bg-green-50',
        border: 'border-green-500',
        title: 'text-green-600'
      },
      forest: {
        card: 'bg-white',
        item: 'bg-green-50',
        border: 'border-green-500',
        title: 'text-green-600'
      },
      midnight: {
        card: 'bg-gray-800',
        item: 'bg-green-900/30',
        border: 'border-green-500',
        title: 'text-green-400'
      }
    };

    const typeOverrides = {
      removed: {
        ocean: { item: 'bg-red-50', border: 'border-red-500', title: 'text-red-600' },
        sunset: { item: 'bg-red-50', border: 'border-red-500', title: 'text-red-600' },
        forest: { item: 'bg-red-50', border: 'border-red-500', title: 'text-red-600' },
        midnight: { item: 'bg-red-900/30', border: 'border-red-500', title: 'text-red-400' }
      },
      changed: {
        ocean: { item: 'bg-yellow-50', border: 'border-yellow-500', title: 'text-yellow-600' },
        sunset: { item: 'bg-yellow-50', border: 'border-yellow-500', title: 'text-yellow-600' },
        forest: { item: 'bg-yellow-50', border: 'border-yellow-500', title: 'text-yellow-600' },
        midnight: { item: 'bg-yellow-900/30', border: 'border-yellow-500', title: 'text-yellow-400' }
      },
      unchanged: {
        ocean: { item: 'bg-gray-50', border: 'border-gray-500', title: 'text-gray-600' },
        sunset: { item: 'bg-gray-50', border: 'border-gray-500', title: 'text-gray-600' },
        forest: { item: 'bg-gray-50', border: 'border-gray-500', title: 'text-gray-600' },
        midnight: { item: 'bg-gray-700', border: 'border-gray-500', title: 'text-gray-400' }
      }
    };

    const base = baseClasses[theme] || baseClasses.ocean;
    const overrides = typeOverrides[type]?.[theme] || typeOverrides[type]?.ocean;
    
    return {
      card: base.card,
      item: overrides?.item || base.item,
      border: overrides?.border || base.border,
      title: overrides?.title || base.title
    };
  };

  return (
    <div className={`min-h-screen p-8 ${getThemeClasses()}`}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold mb-8">JSON Compare Tool</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-lg font-semibold mb-2">JSON Document 1</label>
            <textarea
              value={json1}
              onChange={(e) => setJson1(e.target.value)}
              className={`w-full h-96 p-4 font-mono text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputThemeClasses()}`}
              placeholder="Paste first JSON document here..."
            />
          </div>
          <div>
            <label className="block text-lg font-semibold mb-2">JSON Document 2</label>
            <textarea
              value={json2}
              onChange={(e) => setJson2(e.target.value)}
              className={`w-full h-96 p-4 font-mono text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputThemeClasses()}`}
              placeholder="Paste second JSON document here..."
            />
          </div>
        </div>

        <button
          onClick={handleCompare}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors mb-6"
        >
          Compare JSON
        </button>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {diffResult && (
          <div className="space-y-6">
            <div className={`${getSummaryCardThemeClasses()} rounded-lg shadow-lg p-6`}>
              <h2 className="text-2xl font-bold mb-4">Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`${getSummaryItemThemeClasses('added')} p-4 rounded-lg`}>
                  <div className="text-3xl font-bold">
                    {diffResult.added.length}
                  </div>
                  <div className="text-sm opacity-75">Added</div>
                </div>
                <div className={`${getSummaryItemThemeClasses('removed')} p-4 rounded-lg`}>
                  <div className="text-3xl font-bold">
                    {diffResult.removed.length}
                  </div>
                  <div className="text-sm opacity-75">Removed</div>
                </div>
                <div className={`${getSummaryItemThemeClasses('changed')} p-4 rounded-lg`}>
                  <div className="text-3xl font-bold">
                    {diffResult.changed.length}
                  </div>
                  <div className="text-sm opacity-75">Changed</div>
                </div>
                <div className={`${getSummaryItemThemeClasses('unchanged')} p-4 rounded-lg`}>
                  <div className="text-3xl font-bold">
                    {diffResult.unchanged.length}
                  </div>
                  <div className="text-sm opacity-75">Unchanged</div>
                </div>
              </div>
            </div>

            {diffResult.added.length > 0 && (() => {
              const classes = getDiffSectionThemeClasses('added');
              return (
                <div className={`${classes.card} rounded-lg shadow-lg p-6`}>
                  <h2 className={`text-2xl font-bold mb-4 ${classes.title}`}>Added Fields</h2>
                  <div className="space-y-2">
                    {diffResult.added.map((item, index) => (
                      <div key={index} className={`${classes.item} p-3 rounded border-l-4 ${classes.border}`}>
                        <code className="font-mono text-sm">{item.path}</code>
                        <span className="ml-2">: </span>
                        {renderValue(item.value)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {diffResult.removed.length > 0 && (() => {
              const classes = getDiffSectionThemeClasses('removed');
              return (
                <div className={`${classes.card} rounded-lg shadow-lg p-6`}>
                  <h2 className={`text-2xl font-bold mb-4 ${classes.title}`}>Removed Fields</h2>
                  <div className="space-y-2">
                    {diffResult.removed.map((item, index) => (
                      <div key={index} className={`${classes.item} p-3 rounded border-l-4 ${classes.border}`}>
                        <code className="font-mono text-sm">{item.path}</code>
                        <span className="ml-2">: </span>
                        {renderValue(item.value)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {diffResult.changed.length > 0 && (() => {
              const classes = getDiffSectionThemeClasses('changed');
              return (
                <div className={`${classes.card} rounded-lg shadow-lg p-6`}>
                  <h2 className={`text-2xl font-bold mb-4 ${classes.title}`}>Changed Fields</h2>
                  <div className="space-y-2">
                    {diffResult.changed.map((item, index) => (
                      <div key={index} className={`${classes.item} p-3 rounded border-l-4 ${classes.border}`}>
                        <code className="font-mono text-sm">{item.path}</code>
                        <div className="mt-1">
                          <span className="text-red-600">- {renderValue(item.oldValue)}</span>
                          <br />
                          <span className="text-green-600">+ {renderValue(item.newValue)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {diffResult.unchanged.length > 0 && (() => {
              const classes = getDiffSectionThemeClasses('unchanged');
              return (
                <details className={`${classes.card} rounded-lg shadow-lg p-6`}>
                  <summary className={`text-2xl font-bold cursor-pointer ${classes.title}`}>
                    Unchanged Fields ({diffResult.unchanged.length})
                  </summary>
                  <div className="space-y-2 mt-4">
                    {diffResult.unchanged.map((item, index) => (
                      <div key={index} className={`${classes.item} p-3 rounded`}>
                        <code className="font-mono text-sm">{item.path}</code>
                        <span className="ml-2">: </span>
                        {renderValue(item.value)}
                      </div>
                    ))}
                  </div>
                </details>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

export default JsonComparePage;
